import {
  createElement,
  Fragment,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { DisplayImageElement } from '../../../components/DisplayImageElement.ts';
import { DisplayTextElement } from '../../../components/DisplayTextElement.ts';
import { useGoogleFonts } from '../../../hooks/useGoogleFonts.ts';
import {
  clampSlideBox,
  isFreeformLayout,
  resolveSlideBackgroundType,
  resolveSlideFontFamily,
  SLIDE_FRAME_WINDOW_SIZE,
  type FreeformBackground,
  type SlideBoxPercent,
  type SlideThemeFonts,
} from '../slideFrame.ts';

export type FreeformSlideElement = {
  id: string;
  type?: string | null;
  name?: string | null;
  locked?: boolean | null;
  box?: SlideBoxPercent | null;
  text?: string | null;
  textType?: string | null;
  fontFamily?: string[] | null;
  color?: string | null;
  fontSize?: number | null;
  fontWeight?: string | null;
  textAlign?: string | null;
  verticalAlign?: string | null;
  imageUrl?: string | null;
  fit?: string | null;
};

export type FreeformSlideData = {
  layout?: string | null;
  background?: FreeformBackground | null;
  elements?: FreeformSlideElement[] | null;
};

const VERTICAL_ALIGN: Record<string, CSSProperties['justifyContent']> = {
  TOP: 'flex-start',
  MIDDLE: 'center',
  BOTTOM: 'flex-end',
};

const TEXT_ALIGN: Record<string, CSSProperties['textAlign']> = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
};

export function freeformTextStyle(
  element: FreeformSlideElement,
  themeFonts?: SlideThemeFonts | null,
): CSSProperties {
  const fontSize =
    typeof element.fontSize === 'number' && Number.isFinite(element.fontSize)
      ? element.fontSize
      : 5;
  const verticalAlign = element.verticalAlign || 'TOP';
  const textAlign = element.textAlign || 'LEFT';

  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: VERTICAL_ALIGN[verticalAlign] || 'flex-start',
    textAlign: TEXT_ALIGN[textAlign] || 'left',
    fontFamily: resolveSlideFontFamily(element, themeFonts),
    color: element.color || undefined,
    fontSize: `${fontSize}cqh`,
    fontWeight: element.fontWeight === 'BOLD' ? 700 : 400,
    lineHeight: 1.15,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    overflow: 'visible',
    width: '100%',
    height: '100%',
  };
}

export function freeformElementBoxStyle(
  box: SlideBoxPercent,
  options: { clip: boolean; zIndex: number },
): CSSProperties {
  return {
    position: 'absolute',
    left: `${box.x}%`,
    top: `${box.y}%`,
    width: `${box.w}%`,
    height: `${box.h}%`,
    overflow: options.clip ? 'hidden' : 'visible',
    zIndex: options.zIndex,
  };
}

function collectFontFamilies(
  elements: FreeformSlideElement[],
  themeFonts?: SlideThemeFonts | null,
): string[] {
  const names = new Set<string>();
  const add = (family?: string[] | null) => {
    for (const name of family || []) {
      if (name) {
        names.add(name);
      }
    }
  };
  for (const element of elements) {
    add(element.fontFamily);
  }
  add(themeFonts?.headingFontFamily);
  add(themeFonts?.bodyFontFamily);
  return [...names];
}

function backgroundLayer(
  background: FreeformBackground | null | undefined,
  options?: {
    fill: 'window' | 'contain';
    themeBackdrop?: CSSProperties;
  },
) {
  const type = resolveSlideBackgroundType(background);
  if (type === 'COLOR' && background?.color) {
    return createElement('div', {
      'data-name': 'SLIDE_FREEFORM_BACKGROUND',
      'data-background-type': 'COLOR',
      style: {
        position: 'absolute',
        inset: 0,
        backgroundColor: background.color,
      },
    });
  }
  const paintThemeOnFrame =
    options?.fill === 'contain' && options.themeBackdrop;
  const themeLayer = createElement('div', {
    'data-name': 'SLIDE_FREEFORM_BACKGROUND',
    'data-background-type': 'THEME',
    style: {
      position: 'absolute',
      inset: 0,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      ...(paintThemeOnFrame ? options.themeBackdrop : null),
    },
  });
  if (type === 'IMAGE' && background?.imageUrl) {
    return createElement(
      Fragment,
      null,
      themeLayer,
      createElement(
        'div',
        {
          'data-name': 'SLIDE_FREEFORM_BACKGROUND',
          'data-background-type': 'IMAGE',
          style: {
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            border: 'none',
          },
        },
        createElement(DisplayImageElement, {
          mode: 'img',
          imageUrl: background.imageUrl,
          objectFit: 'cover',
          style: {
            position: 'absolute',
            inset: 0,
          },
        }),
      ),
    );
  }
  return themeLayer;
}

function renderElement(
  element: FreeformSlideElement,
  index: number,
  themeFonts?: SlideThemeFonts | null,
): ReactElement | null {
  const box = clampSlideBox(element.box || undefined);
  if (!box) {
    return null;
  }
  const zIndex = index + 1;
  if (element.type === 'IMAGE') {
    return createElement(
      'div',
      {
        key: element.id,
        'data-name': 'SLIDE_FREEFORM_ELEMENT',
        'data-element-type': 'IMAGE',
        'data-id': element.id,
        'data-stack-index': String(index),
        style: freeformElementBoxStyle(box, { clip: true, zIndex }),
      },
      createElement(DisplayImageElement, {
        mode: 'img',
        imageUrl: element.imageUrl,
        objectFit: element.fit === 'COVER' ? 'cover' : 'contain',
        alt: '',
        dataName: 'SLIDE_FREEFORM_IMAGE',
        dataId: element.id,
      }),
    );
  }

  return createElement(
    'div',
    {
      key: element.id,
      'data-name': 'SLIDE_FREEFORM_ELEMENT',
      'data-element-type': 'TEXT',
      'data-id': element.id,
      'data-stack-index': String(index),
      style: freeformElementBoxStyle(box, { clip: false, zIndex }),
    },
    createElement(DisplayTextElement, {
      text: element.text,
      style: freeformTextStyle(element, themeFonts),
      dataName: 'SLIDE_FREEFORM_TEXT',
      dataId: element.id,
    }),
  );
}

export function FreeformSlideFrame({
  slideId,
  slideData,
  entranceClassName,
  fill = 'window',
  themeFonts,
  themeBackdrop,
  children,
}: {
  slideId?: string | null;
  slideData: FreeformSlideData | null;
  entranceClassName?: string;
  fill?: 'window' | 'contain';
  themeFonts?: SlideThemeFonts | null;
  themeBackdrop?: CSSProperties;
  children?: ReactNode;
}): ReactElement | null {
  const elements = slideData?.elements || [];
  const fonts = collectFontFamilies(elements, themeFonts);
  useGoogleFonts({ fontFamily: fonts.length ? fonts : undefined });

  if (!isFreeformLayout(slideData?.layout)) {
    return null;
  }

  const frameStyle: CSSProperties =
    fill === 'window'
      ? {
          ...SLIDE_FRAME_WINDOW_SIZE,
          containerType: 'size',
          overflow: 'hidden',
          position: 'relative',
        }
      : {
          width: '100%',
          height: '100%',
          containerType: 'size',
          overflow: 'hidden',
          position: 'relative',
          aspectRatio: `${16} / ${9}`,
        };

  const frame = createElement(
    'div',
    {
      'data-name': 'SLIDE_FREEFORM_FRAME',
      'data-slide-id': slideId || undefined,
      className: entranceClassName,
      style: frameStyle,
    },
    backgroundLayer(slideData?.background, { fill, themeBackdrop }),
    ...elements
      .map((element, index) => renderElement(element, index, themeFonts))
      .filter((node): node is ReactElement => node != null),
  );

  if (fill === 'contain') {
    return createElement(Fragment, null, frame, children);
  }

  return createElement(
    Fragment,
    null,
    createElement(
      'div',
      {
        'data-name': 'SLIDE_FREEFORM_WINDOW',
        className:
          'pointer-events-none fixed inset-0 z-0 flex items-center justify-center',
      },
      createElement(
        'div',
        {
          className: 'pointer-events-auto h-full w-full',
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        },
        frame,
      ),
    ),
    children,
  );
}
